import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPlaces, getPlacesNearby } from '../services/PlacesAPI';
import PlaceCard from '../components/PlaceCard';
import './Explore.css';
import NavBar from "../components/NavBar";
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { Box } from "@mui/material";
import GoogleMaps, { MapViewport } from '../components/GoogleMaps';
import { getGooglePhotoUrl } from '../utils/photoUtils';

export type HoverSource = 'card' | 'map';

export interface RoamEasyPlace {
    id: string;
    name: string | null | undefined;
    imageUrl: string | null;
    city: string | null;
    country: string | null;
    saved: boolean;
    location: google.maps.LatLngLiteral | null;
}

export const numberOfPlacesWhenLoading = 20;

const zoomToRadius = (zoom: number) => {
    if (zoom <= 7) return 50000;
    if (zoom <= 9) return 35000;
    if (zoom <= 11) return 20000;
    if (zoom <= 13) return 12000;
    if (zoom <= 15) return 7000;
    if (zoom <= 17) return 3500;
    return 2000;
};

const hasViewportShifted = (prev: MapViewport | null, next: MapViewport) => {
    if (!prev) return true;
    const latDiff = Math.abs(prev.center.lat - next.center.lat);
    const lngDiff = Math.abs(prev.center.lng - next.center.lng);
    const zoomDiff = Math.abs((prev.zoom ?? 0) - (next.zoom ?? 0));
    return latDiff > 0.02 || lngDiff > 0.02 || zoomDiff >= 1;
};

const isLngWithinBounds = (lng: number, bounds: google.maps.LatLngBoundsLiteral) => {
    const { east, west } = bounds;
    if (east >= west) {
        return lng >= west && lng <= east;
    }
    return lng >= west || lng <= east;
};

const isLocationWithinBounds = (
    location: google.maps.LatLngLiteral,
    bounds: google.maps.LatLngBoundsLiteral
) => {
    const { lat, lng } = location;
    return lat <= bounds.north && lat >= bounds.south && isLngWithinBounds(lng, bounds);
};

const GEOLOCATION_OPTIONS: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
};

export default function Explore(){
    const [places, setPlaces] = useState<RoamEasyPlace[]>([]);
    const [city, setCity] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isMapSyncing, setIsMapSyncing] = useState(false);
    const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
    const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
    const [viewportResetKey, setViewportResetKey] = useState(0);
    const [mapHoveredPlaceId, setMapHoveredPlaceId] = useState<string | null>(null);
    const [currentViewport, setCurrentViewport] = useState<MapViewport | null>(null);
    const savedPlaces = useSelector((state: RootState) => state.savedPlaces);
    const lastViewportRef = useRef<MapViewport | null>(null);
    const viewportDebounceRef = useRef<number | null>(null);
    const initialDataLoadedRef = useRef(false);
    const hasRegisteredInitialViewportRef = useRef(false);
    const location = useLocation()
    const { state } = location;
    const {SelectedValue} = state || {};
    type GooglePlaceResult = google.maps.places.Place | google.maps.places.PlaceResult;

    const normalizePlaces = useCallback((rawPlaces: GooglePlaceResult[]) => {
        return rawPlaces.map((p) => {
            const extendedPlace = p as google.maps.places.PlaceResult & {
                id?: string;
                displayName?: string | { text?: string };
            };
            const placeId = extendedPlace.id ?? '';
            const derivedName =
                (typeof extendedPlace.displayName === 'string'
                    ? extendedPlace.displayName
                    : extendedPlace.displayName?.text) ??
                extendedPlace.name ??
                (extendedPlace as google.maps.places.PlaceResult & { formattedAddress?: string }).formattedAddress ??
                'Unknown Place';
            let imageUrl = null;
            let city = null;
            let country = null;
            let location: google.maps.LatLngLiteral | null = null;
            if (p.photos && p.photos[0]) {
                imageUrl = getGooglePhotoUrl(p.photos[0]);
            }
            const rawLocation = (p as google.maps.places.PlaceResult & {
                location?: google.maps.LatLng | google.maps.LatLngLiteral;
            }).location;
            if (rawLocation) {
                if (typeof (rawLocation as google.maps.LatLng).lat === 'function') {
                    const latLng = rawLocation as google.maps.LatLng;
                    const latFn = latLng.lat();
                    const lngFn = latLng.lng();
                    location = { lat: latFn, lng: lngFn };
                } else if ('lat' in rawLocation && 'lng' in rawLocation) {
                    const literal = rawLocation as google.maps.LatLngLiteral;
                    location = { lat: literal.lat, lng: literal.lng };
                }
            }
            const addressComponents = (p as google.maps.places.PlaceResult & {
                addressComponents?: google.maps.places.AddressComponent[];
            }).addressComponents;
            addressComponents?.forEach((element) => {
                if (element.types.includes('locality')) {
                    city = element.shortText;
                } else if (element.types.includes('country')) {
                    country = element.shortText;
                }
           });
           return { id: placeId, name: derivedName, imageUrl, city, country, saved: false, location };
        }).filter((place) => place.id && place.name && place.location);
    }, []);
    const handleGeoSuccess = useCallback((pos: GeolocationPosition) => {
        const { latitude, longitude } = pos.coords;
        fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyAl17GAyvczj9b5vYEIK4frIwvQBQ_z_j4&result_type=locality&loading=async`)
            .then((res) => res.json())
            .then((data) => {
              setCity(data.results[0].formatted_address);
            }).catch((error) => console.error('Failed to reverse geocode', error));
      }, []);
    const handleGeoError = useCallback((err: GeolocationPositionError) => {
        console.warn(`ERROR(${err.code}): ${err.message}`);
      }, []);

    const loadPlaces = useCallback(async (
        fetcher: () => Promise<GooglePlaceResult[]>,
        options?: { showSkeleton?: boolean; resetViewport?: boolean }
    ) => {
        const showSkeleton = options?.showSkeleton ?? false;
        const resetViewport = options?.resetViewport ?? showSkeleton;
        if (resetViewport) {
            lastViewportRef.current = null;
            hasRegisteredInitialViewportRef.current = false;
        }
        if (showSkeleton) {
            setIsLoading(true);
        } else {
            setIsMapSyncing(true);
        }
        try {
            const results = await fetcher();
            const updatedPlaces = normalizePlaces(results);
            setPlaces(updatedPlaces);
            if (resetViewport) {
                setViewportResetKey((prev) => prev + 1);
            }
            initialDataLoadedRef.current = true;
        } catch (error) {
            console.error('Failed to load places', error);
        } finally {
            if (showSkeleton) {
                setIsLoading(false);
            } else {
                setIsMapSyncing(false);
            }
        }
    }, [normalizePlaces, setViewportResetKey]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.permissions
              .query({ name: "geolocation" })
              .then(function (result) {
                if (result.state === "granted") {
                  //If granted then you can directly call your function here
                  navigator.geolocation.getCurrentPosition(handleGeoSuccess, handleGeoError, GEOLOCATION_OPTIONS);
                } else if (result.state === "prompt") {
                  //If prompt then the user will be asked to give permission
                  navigator.geolocation.getCurrentPosition(handleGeoSuccess, handleGeoError, GEOLOCATION_OPTIONS);
                } else if (result.state === "denied") {
                  //If denied then you have to show instructions to enable location
                }
              });
          } else {
            console.log("Geolocation is not supported by this browser.");
          }
        const placeName = SelectedValue? SelectedValue.description : city;
        if (!placeName) {
            return;
        }
        loadPlaces(
            () => getPlaces(placeName + ' places to visit'),
            { showSkeleton: !initialDataLoadedRef.current, resetViewport: true }
        );
      }, [SelectedValue, city, loadPlaces, handleGeoSuccess, handleGeoError]);

      // Show places that were saved by the user as saved
      useEffect(() => {
        if(!savedPlaces.loading){
          setPlaces((prev: RoamEasyPlace[]) => {
            const updatedPlaces = prev.map((p) => (
              savedPlaces.items[p.id] ? {...p, saved: true} : {...p, saved: false}
            ));

            // Only update state if there are changes
            return JSON.stringify(prev) === JSON.stringify(updatedPlaces) ? prev : updatedPlaces;
          });
        }
      },[places, savedPlaces.items, savedPlaces.loading]);

      useEffect(() => {
        return () => {
            if (viewportDebounceRef.current) {
                window.clearTimeout(viewportDebounceRef.current);
            }
        };
      }, []);

      useEffect(() => {
        if (mapHoveredPlaceId && !places.some((place) => place.id === mapHoveredPlaceId)) {
            setMapHoveredPlaceId(null);
        }
      }, [mapHoveredPlaceId, places]);

      function onSaved(placeId: string) {
        setPlaces((prev: RoamEasyPlace[]) => {
          return prev.map((p)=> (
            p.id === placeId ? {...p, saved: !p.saved} : p
          ));
        })
      }

      const handlePlaceHover = (placeId: string | null, source: HoverSource = 'card') => {
        setHoveredPlaceId(placeId);
        if (source === 'map') {
            setMapHoveredPlaceId(placeId);
        }
      };

      const handlePlaceSelect = (placeId: string | null) => {
        setSelectedPlaceId((prev) => (prev === placeId ? null : placeId));
      };

      const activePlaceId = hoveredPlaceId ?? selectedPlaceId;
      const infoWindowPlaceId = selectedPlaceId ?? mapHoveredPlaceId;
      const placesInViewport = useMemo(() => {
        const bounds = currentViewport?.bounds;
        if (!bounds) {
            return places;
        }
        return places.filter(
            (place) => place.location && isLocationWithinBounds(place.location, bounds)
        );
      }, [places, currentViewport]);

      const requestPlacesForViewport = useCallback((viewport: MapViewport) => {
        return loadPlaces(
            () => getPlacesNearby(viewport.center, zoomToRadius(viewport.zoom)),
            { showSkeleton: false }
        );
      }, [loadPlaces]);

      const handleViewportChanged = useCallback((viewport: MapViewport) => {
        setCurrentViewport(viewport);
        if (!initialDataLoadedRef.current) {
            return;
        }
        if (!hasRegisteredInitialViewportRef.current) {
            hasRegisteredInitialViewportRef.current = true;
            lastViewportRef.current = viewport;
            return;
        }
        if (!hasViewportShifted(lastViewportRef.current, viewport)) {
            return;
        }
        lastViewportRef.current = viewport;
        if (viewportDebounceRef.current) {
            window.clearTimeout(viewportDebounceRef.current);
        }
        viewportDebounceRef.current = window.setTimeout(() => {
            requestPlacesForViewport(viewport);
        }, 600);
      }, [requestPlacesForViewport]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: `calc(100 * var(--vh))` }}>
            <NavBar/>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: { xs: 'unset', md: 1 },
                  width: { xs: '100%', md: '50%' },
                  overflowY: 'auto',
                  padding: '16px',
                  paddingBottom: `calc(16px + var(--safe-area-inset-bottom))`,
                  scrollbarWidth: "none",
                }}
              >
                <Grid
                  container
                  rowSpacing={2}
                  spacing={1}
                  className="PlaceCard"
                  style={{
                    opacity: isMapSyncing ? 0.4 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {isLoading ? (
                    <>
                      {Array.from({ length: numberOfPlacesWhenLoading }).map((_, index) => (
                        <Card
                          key={index}
                          sx={{ maxWidth: 350, minWidth: 250, marginRight: 5, my: 5, ml: 5 }}
                        >
                          <Skeleton variant="rectangular" width={250} height={300} />
                        </Card>
                      ))}
                    </>
                  ) : (
                    placesInViewport.map((p) => {
                      if (p.name) {
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={4} key={p.id}>
                            <Box
                              sx={{
                                  paddingRight: '25px',
                              }}
                            >
                              <PlaceCard
                                id={p.id}
                                name={p.name}
                                imageUrl={p.imageUrl}
                                city={p.city}
                                country={p.country}
                                saved={p.saved}
                                isSelected={activePlaceId === p.id}
                                onHover={(placeId) => handlePlaceHover(placeId, 'card')}
                                onSelect={handlePlaceSelect}
                                onSavedToggle={onSaved}
                              />
                            </Box>
                          </Grid>
                        );
                      }
                      return null;
                    })
                  )}
                </Grid>
              </Box>
              <Box
                sx={{
                  flex: { xs: 'unset', md: 1 },
                  width: { xs: '100%', md: '50%' },
                  minHeight: { xs: '300px', md: 'auto' },
                  borderTop: { xs: '1px solid #e0e0e0', md: 'none' },
                  borderLeft: { md: '1px solid #e0e0e0' },
                  display: 'flex',
                }}
              >
                <GoogleMaps
                  places={places}
                  activePlaceId={activePlaceId}
                  onPlaceHover={handlePlaceHover}
                  infoWindowPlaceId={infoWindowPlaceId}
                  onPlaceSelect={handlePlaceSelect}
                  onViewportChanged={handleViewportChanged}
                  viewportResetKey={viewportResetKey}
                />
              </Box>
            </Box>
        </div>
    );
}
